<template>
  <div v-if="isLoadingFetch" class="flex items-center gap-1.5 text-zinc-500">
    <Icon class="animate-spin text-xl" name="progress_activity" />
    <span>{{ __('Loading...') }}</span>
  </div>

  <div v-else class="space-y-6 md:space-y-8">
    <div class="space-y-2.5">
      <div class="flex items-center gap-2 md:gap-4">
        <Icon
          class="!text-3xl text-primary"
          name="business_center"
          type="filled"
        />
        <h1 class="text-2xl font-medium text-zinc-800 md:text-4xl">
          {{ __('What type of job are you looking for?') }}
        </h1>
      </div>

      <p class="text-sm font-medium text-zinc-800 lg:text-lg">
        {{ __('Tell us more about your career preferences.') }}
      </p>
    </div>

    <form class="space-y-8" @submit.prevent="handleSubmit">
      <div class="grid gap-4 md:grid-cols-2">
        <InputCareers
          v-if="fields.role.visible"
          id="role"
          v-model:value="fields.role.value"
          v-model:errors="fields.role.errors"
          :label="__('What roles are you seeking?')"
          :placeholder="__('Add desired roles')"
          :required="fields.role.mandatory"
          is-new-options-allowed
        />
        <div class="[&_input::placeholder]:text-base [&_.mt-4.flex.flex-wrap]:!mt-2">
          <label class="mb-2 block text-sm text-[#58585E]"
          >{{ __('Select type of job:') }}
          </label>
          <FormMultiselect
              v-model="fields.type.value"
              :values="fields.type.options"
              :clearable="true"
              :multiple="true"
              type="static"
              class="mt-2"
              :errors="fields.type.errors"
              :placeholder="__('Add job type')"
              :required="fields.type.mandatory"
          />
        </div>
        <InputLocation
          v-if="fields.location.visible"
          v-model:value="fields.location.value"
          v-model:errors="fields.location.errors"
          :label-country="__(`Select the countries where you'd like to work`)"
          :label-city="__(`Select the cities where you'd like to work`)"
          :placeholder-country="__('Add country')"
          :placeholder-city="__('Add city')"
          :options="fields.location.options"
          :required="fields.location.mandatory"
          class="md:col-span-2"
        />
      </div>

      <div class="flex items-center justify-between">
        <Button
          class="flex items-center gap-1.5"
          variant="ghost"
          @click="handleBackButtonClick"
        >
          <Icon class="text-lg" name="arrow_back_ios_new" />
          <span>{{ __('Go Back') }}</span>
        </Button>

        <Button class="ml-auto flex items-center gap-1.5" type="submit">
          <Icon
            v-if="isLoadingUpdate"
            class="animate-spin text-lg"
            name="progress_activity"
          />

          <span>{{ __('Next') }}</span>
        </Button>
      </div>
    </form>
  </div>
</template>

<script setup>
  import { onMounted, ref } from 'vue';
  import { useToast } from 'vue-toastification';
  import axios from 'axios';
  import { __ } from '@/helpers';
  import { Button } from '@/components/block/button';

  import { InputCareers } from '@/components/form/input-careers';
  import { InputCombobox } from '@/components/form/input-combobox';
  import { InputLocation } from '@/components/form/input-location';
  import { FormMultiselect } from '@/components/global';
  import Icon from '@/components/global/Icon.vue';

  const emits = defineEmits(['back', 'next']);

  const toast = useToast();

  const fields = ref({
    role: {
      visible: false,
      mandatory: false,
      value: [],
      errors: [],
      options: []
    },
    type: {
      visible: false,
      mandatory: false,
      value: [],
      errors: [],
      options: []
    },
    location: {
      visible: false,
      mandatory: false,
      default_country: [],
      value: [],
      errors: [],
      options: []
    }
  });
  const isLoadingFetch = ref(false);
  const isLoadingUpdate = ref(false);

  function isValid(value) {
    if (!value) {
      return false;
    }

    if (Array.isArray(value)) {
      return value.length > 0;
    }

    if (typeof value === 'object') {
      for (const prop in value) {
        if (value[prop] === null) {
          return false;
        }
      }
      return true;
    }

    return true;
  }

  async function fetchData() {
    isLoadingFetch.value = true;

    try {
      const response = await axios.get(
        route('onboarding.step', { slug: 'career' })
      );

      Object.entries(response.data).forEach(([key, value]) => {
        if (fields.value[key]) {
          fields.value[key] = { ...fields.value[key], ...value };
        }
      });
    } catch {
      toast.error(
        __('An unexpected error has occured. Please try again later.')
      );
    }

    isLoadingFetch.value = false;
  }

  function handleBackButtonClick() {
    emits('back');
  }

  async function handleSubmit() {
    const requiredFieldKeys = Object.keys(fields.value).filter(
      (key) => fields.value[key].visible && fields.value[key].mandatory
    );
    let firstInvalidField;

    requiredFieldKeys.forEach((key) => {
      if (!isValid(fields.value[key].value)) {
        fields.value[key].errors = [__('This field is required')];

        const element = document.getElementById(key);

        if (element && !firstInvalidField) {
          firstInvalidField = element;
        }
      } else {
        fields.value[key].errors = [];
      }
    });

    if (
      Object.values(fields.value).some(
        (field) => field.errors && field.errors.length
      )
    ) {
      if (firstInvalidField) {
        firstInvalidField.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }
      return;
    }

    isLoadingUpdate.value = true;

    const customCareerOptions = fields.value.role.value.filter(
      (career) => career.id === 'custom-option'
    );

    if (customCareerOptions.length) {
      try {
        const response = await axios.post(route('redesign.cv.career.create'), {
          careers: customCareerOptions
        });

        fields.value.role.value = fields.value.role.value.filter(
          (career) => career.id !== 'custom-option'
        );

        fields.value.role.value = [
          ...fields.value.role.value,
          ...response.data.careers
        ];
      } catch {
        toast.error(
          __('An unexpected error has occured. Please try again later.')
        );
      }
    }

    const payload = Object.keys(fields.value).reduce((acc, key) => {
      if (fields.value[key].visible) {
        acc[key] = fields.value[key].value;
      }
      return acc;
    }, {});

    try {
      await axios.post(route('onboarding.step', { slug: 'career' }), payload);

      emits('next');
    } catch (error) {
      if (error?.response?.data?.errors) {
        Object.keys(error.response.data.errors).forEach((key) => {
          if (fields.value[key]) {
            fields.value[key].errors = error.response.data.errors[key];
          }
        });
      } else {
        toast.error(
          __('An unexpected error has occured. Please try again later.')
        );
      }
    }

    isLoadingUpdate.value = false;
  }

  onMounted(async () => {
    await fetchData();
  });
</script>
